"use client";

// Verbatim port of the private
// `packages/sdk/react/src/tools/use-tool-auto-open.ts` (plan 030); only
// the context import path changed.
import { c as _c } from "react/compiler-runtime";
import { use, useEffect, useId, useRef, useState } from "react";
import { ToolViewContext } from "./tool-view-context";
type UseToolAutoOpenOptions = {
  /**
   * When true, the collapsible defaults to open even without auto-open context.
   * Useful for tool results that should be visible immediately (e.g., employee cards).
   * @default false
   */
  defaultOpen?: boolean;
};

/**
 * Manages open/close state for tool collapsible views.
 *
 * Integrates with `ToolViewContext` to support auto-open behavior (only the
 * most recently registered active tool opens automatically) and manual
 * override (once the user toggles, they own the state).
 */
export function useToolAutoOpen(isActive, t0) {
  const $ = _c(14);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    defaultOpen: t2
  } = options;
  const defaultOpen = t2 === undefined ? false : t2;
  const id = useId();
  const ctx = use(ToolViewContext);
  const hasRegistered = useRef(false);
  const [manualOpen, setManualOpen] = useState(defaultOpen);
  const autoOpenEnabled = ctx?.meta.autoOpenEnabled ?? false;
  const setAutoOpen = ctx?.actions.setAutoOpen;
  let t3;
  let t4;
  if ($[2] !== autoOpenEnabled || $[3] !== id || $[4] !== isActive || $[5] !== setAutoOpen) {
    t3 = () => {
      if (isActive && autoOpenEnabled && setAutoOpen && !hasRegistered.current) {
        setAutoOpen(id);
        hasRegistered.current = true;
      }
    };
    t4 = [isActive, autoOpenEnabled, setAutoOpen, id];
    $[2] = autoOpenEnabled;
    $[3] = id;
    $[4] = isActive;
    $[5] = setAutoOpen;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  const isManual = ctx?.state.manuallyOpened.has(id) ?? false;
  const isAutoOpen = ctx?.meta.autoOpenEnabled ? ctx.state.autoOpenId === id : defaultOpen;
  const isOpen = isManual ? manualOpen : isAutoOpen;
  let t5;
  if ($[8] !== ctx?.actions || $[9] !== id) {
    t5 = open => {
      setManualOpen(open);
      ctx?.actions.registerManualOpen(id);
    };
    $[8] = ctx?.actions;
    $[9] = id;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleOpenChange = t5;
  let t6;
  if ($[11] !== handleOpenChange || $[12] !== isOpen) {
    t6 = {
      isOpen,
      handleOpenChange
    };
    $[11] = handleOpenChange;
    $[12] = isOpen;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}